import { useId } from "react";
import { CapturedValues } from "@/components/BondRecord";
import type { BondRenderField } from "@/lib/bondFields";
import {
  choicesFor,
  declarationFor,
  type ChangeChoice,
} from "@/lib/bondRepeatRequest";
import type { BondStep } from "@/lib/brokerBonds";

// BRK-S162 — the repeat request's Summary chip.
//
// An insured who already holds a standing program has answered all of this
// before. Until now a repeat bid bond re-rendered every fields step with PMS's
// prefill, so the broker walked past their own business details and their own
// indemnitor roster, one Continue at a time, to change nothing.
//
// So: show what Signal holds, and ask ONE question. Only what they say changed
// opens for edit — the spine behind this chip is built from the answer
// (`bondChips`'s `repeat` option), which is why the question lives here rather
// than as a checkbox on each step.
//
// ── READ-ONLY, AND THAT IS THE POINT ────────────────────────────────────────
//
// Nothing on this card is an input except the question. A summary with editable
// fields would be a second capture surface for values the fields steps already
// own, and the broker would have two places to change one thing.
//
// ── THE EIN IS NOT A CONTROL ANYWHERE ───────────────────────────────────────
//
// It is shown here, with a note, and it is removed from the business step's
// editable fields entirely (`withoutImmutableFields`). PMS makes a set EIN
// immutable on every write path (PMS-S462, a 400 `EIN_IMMUTABLE`), so a control
// for it would be an input whose only possible use is a rejected save. The
// value still travels on the step save untouched, which is what keeps that save
// from looking like a change.
//
// ── WHAT THE ANSWER IS NOT ──────────────────────────────────────────────────
//
// It is not a claim about the insured's RECORD. "Nothing changed" closes steps
// on THIS order; it writes nothing to the account and asserts nothing about it.

/** One summarised step, as the page resolves it. */
export interface RepeatSummarySection {
  step: BondStep;
  /** The step's render fields — the roster's expanded, per-owner ones
   *  included, so the summary reads like the form that produced it. */
  fields: readonly BondRenderField[];
  /** That step's values, keyed the way `fields` names them. */
  values: Record<string, unknown>;
}

export function BondRepeatSummary({
  steps,
  sections,
  ein,
  choice,
  onChoice,
}: {
  /** The configuration's steps — what decides whether the question has an
   *  indemnitor half to ask about at all. */
  steps: readonly BondStep[];
  sections: readonly RepeatSummarySection[];
  /** The insured's EIN, when the order carries one. */
  ein: string | null;
  /** `null` until the broker answers — which is what keeps the spine behind
   *  this chip at its shortest until they do. */
  choice: ChangeChoice | null;
  onChoice: (choice: ChangeChoice) => void;
}) {
  const group = useId();
  const choices = choicesFor(steps);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-brk-text">
          What we have on file
        </h2>
        <p className="text-sm text-brk-dim">
          You’ve been pre-approved, so we already hold this insured’s details.
          Check them over — you only need to re-enter what has changed.
        </p>
      </div>

      {ein && (
        <div className="rounded-xl border border-brk-border bg-brk-bg p-4">
          <dl className="flex flex-col gap-0.5">
            <dt className="text-xs uppercase tracking-wide text-brk-dim">
              EIN
            </dt>
            <dd className="break-words font-medium text-brk-text">{ein}</dd>
          </dl>
          {/* Says WHY it is not editable rather than leaving a disabled box to
              be puzzled over. It is the insured's federal tax id: a different
              one is a different insured. */}
          <p className="mt-1 text-xs text-brk-dim">
            An EIN can’t be changed — it identifies the business. If this is
            wrong, message us and we’ll sort it out.
          </p>
        </div>
      )}

      {sections.map((section) => (
        <section
          key={section.step.key}
          aria-labelledby={`${group}-${section.step.key}`}
          className="flex flex-col gap-3 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-5"
        >
          <h3
            id={`${group}-${section.step.key}`}
            className="text-sm font-semibold text-brk-text"
          >
            {section.step.title}
          </h3>
          {/* The SAME renderer the submitted record uses, so one set of
              formatting decisions serves both — a date, a currency figure and
              an option label read identically wherever a broker meets them. */}
          <CapturedValues
            fields={section.fields}
            values={section.values}
            emptyNote="Nothing on file for this section yet — you’ll be asked for it."
          />
        </section>
      ))}

      <fieldset className="flex flex-col gap-3">
        <legend className="text-base font-semibold text-brk-text">
          Has any of this information changed?
        </legend>
        <div className="flex flex-col gap-2">
          {choices.map((option) => (
            <label
              key={option.value}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                choice === option.value
                  ? "border-brk-accent bg-brk-accent/5"
                  : "border-brk-border bg-brk-panel hover:bg-brk-bg"
              }`}
            >
              <input
                type="radio"
                name={`${group}-changed`}
                className="mt-1"
                value={option.value}
                checked={choice === option.value}
                onChange={() => onChoice(option.value)}
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm font-medium text-brk-text">
                  {option.label}
                </span>
                {/* What picking it DOES — which is the thing being chosen. */}
                <span className="text-xs text-brk-dim">{option.hint}</span>
              </span>
            </label>
          ))}
        </div>
        {choice !== null && (
          <p role="status" className="text-sm text-brk-dim">
            {declarationFor(choice).business ||
            declarationFor(choice).indemnitors
              ? "We’ll open just those sections next."
              : "We’ll take you straight to the bond details."}
          </p>
        )}
      </fieldset>
    </div>
  );
}
